/**
 * Data transformation utilities for ActionPlan module
 */

import { CalendarEvent, ApiActionPlan, DropDownOption } from '../types';
import { COLOR_MAPPING, STATUS_DISPLAY_TO_API, DEFAULT_USER_ID } from '../constants';
import { FilterItem } from '../components/TableSection';
import { ActionPlanItem } from '../types';

/**
 * Format name from email address and GPID for display
 */
export const formatNameFromEmail = (email: string | undefined, gpid: string | undefined): string => {
  // If we have an email, extract and format the name part
  if (email && email.includes('@')) {
    const namePart = email.split('@')[0]; // Get "john.smith" from "john.smith@company.com"
    const nameParts = namePart.split('.'); // Split into ["john", "smith"]
    const formattedName = nameParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()) // Capitalize each part
      .join(' '); // Join with space
    return formattedName.replace(/\sContractor$/i, '').trim(); // Remove "Contractor" suffix if present
  }

  // If we only have GPID, display it
  if (gpid && gpid.trim()) {
    return gpid.trim();
  }

  // Return empty string if neither is available
  return '';
};

/**
 * Format filter display text: replace underscores with spaces and trim
 */
const formatFilterDisplayText = (raw?: string | null): string => {
  if (raw === null || raw === undefined || raw === '') return '';
  const str = String(raw);
  // Replace underscores with spaces
  const withSpaces = str.replace(/_/g, ' ');
  // Trim but preserve original casing to avoid unexpected capitalization changes
  const trimmed = withSpaces.trim();
  if (!trimmed) return '';
  return trimmed; // keep original casing (e.g., 'general new')
};

/**
 * Transform API action plan response to calendar event
 */
export const transformApiActionPlanToCalendarEvent = (actionPlan: ApiActionPlan): CalendarEvent => {

  const rawTarget = actionPlan.TARGET_DATE;
  let targetDate: Date;

  // Parse date WITHOUT timezone conversion
  if (typeof rawTarget === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawTarget)) {
    // Extract date components from ISO string
    const match = rawTarget.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1; // Month is 0-indexed
      const day = Number(match[3]);
      targetDate = new Date(year, month, day);
    } else {
      // Fallback for unexpected format
      targetDate = new Date();
    }
  } else {
    // Fallback for non-string or invalid format
    targetDate = new Date();
  }

  // Determine color based on status and priority
  let color = COLOR_MAPPING.DEFAULT;
  let isDone = false;

  if (actionPlan.STATUS === 'completed') {
    isDone = true;
    color = COLOR_MAPPING.COMPLETED;
  } else if (actionPlan.PRIORITY === 'High') {
    color = COLOR_MAPPING.HIGH_PRIORITY;
  } else if (actionPlan.PRIORITY === 'Medium') {
    color = COLOR_MAPPING.MEDIUM_PRIORITY;
  }

  return {
    id: String(actionPlan.ID),
    title: actionPlan.TITLE,
    date: targetDate,
    color,
    isDone,
    removable: false,
    actionPlanData: actionPlan, // Include full action plan data
  };
};

/**
 * Build filter options dynamically from action plan data
 */
export const buildFilterOptions = (
  actionPlanData: ActionPlanItem[],
  currentFilters: Record<string, any>,
  ownerOptions?: Array<{ id: string; displayText: string; originalValue: string }>,
  assigneeOptions?: Array<{ id: string; displayText: string; originalValue: string }>
): FilterItem[] => {
  // Helper to read fields from API response that may be uppercase (API) or
  // lowercase (some endpoints/environments). Tries the key as-is, then lower,
  // then upper.
  const read = (item: any, key: string) => {
    if (!item) return undefined;
    if (item[key] !== undefined) return item[key];
    const lower = key.toLowerCase();
    if (item[lower] !== undefined) return item[lower];
    const upper = key.toUpperCase();
    if (item[upper] !== undefined) return item[upper];
    return undefined;
  };
  // Get unique values from current data for dynamic options
  const uniqueSources = ['All', ...new Set(actionPlanData.map(item => read(item, 'SOURCE')).filter(Boolean))];
  const uniqueTypes = ['All', ...new Set(actionPlanData.map(item => read(item, 'TYPE')).filter(Boolean))];
  
  // For status, always include all possible status options regardless of current data
  const uniqueStatuses = ['All', 'In Progress', 'Completed', 'Declined', 'Overdue'];
  console.log('Status filter - uniqueStatuses:', uniqueStatuses);
  console.log('Status filter - currentFilters:', currentFilters);
  
  const uniquePriorities = ['All', ...new Set(actionPlanData.map(item => read(item, 'PRIORITY')).filter(Boolean))];
  
  // Build assignee map: GPID -> formatted name
  const assigneeMap = new Map<string, string>();
  actionPlanData.forEach(item => {
    const assigneeGpid = read(item, 'ASSIGNEE_GPID');
    const assigneeEmail = read(item, 'ASSIGNEE_EMAIL') || read(item, 'ASSIGNEE_NAME');
    if (assigneeGpid) {
      const displayName = formatNameFromEmail(assigneeEmail, assigneeGpid);
      if (displayName) {
        assigneeMap.set(assigneeGpid, displayName);
      }
    }
  });
  const uniqueAssignees = Array.from(assigneeMap.entries());
  
  // Build owner map: GPID -> formatted name
  const ownerMap = new Map<string, string>();
  actionPlanData.forEach(item => {
    const ownerGpid = read(item, 'OWNER_GPID');
    const ownerEmail = read(item, 'OWNER_EMAIL') || read(item, 'OWNER_NAME');
    if (ownerGpid) {
      const displayName = formatNameFromEmail(ownerEmail, ownerGpid);
      if (displayName) {
        ownerMap.set(ownerGpid, displayName);
      }
    }
  });
  const uniqueOwners = Array.from(ownerMap.entries());

  return [
    {
      key: 'source',
      label: 'Source',
      options: uniqueSources.map((value, index) => ({ 
        id: index, 
        displayText: value === 'All' ? value : formatFilterDisplayText(value),
        originalValue: value // Store original value for API calls
      })),
      selectedOptionId: uniqueSources.findIndex(v => v === currentFilters.source),
      placeholder: 'Select Source'
    },
    {
      key: 'type',
      label: 'Type',
      options: uniqueTypes.map((value, index) => ({ 
        id: index, 
        displayText: value === 'All' ? value : formatFilterDisplayText(value),
        originalValue: value // Store original value for API calls
      })),
      selectedOptionId: uniqueTypes.findIndex(v => v === currentFilters.type),
      placeholder: 'Select Type'
    },
    {
      key: 'targetDate',
      label: 'End Date',
      placeholder: 'End Date',
      // Mark this filter to use custom date picker rendering
      customRenderer: 'datePicker',
    },
    {
      key: 'status',
      label: 'Status',
      options: uniqueStatuses.map((value, index) => {
        // Map display text to API values for the new status options
        let apiValue = value;
        if (value === 'In Progress') apiValue = 'in_progress';
        else if (value === 'Completed') apiValue = 'completed';
        else if (value === 'Declined') apiValue = 'declined';
        else if (value === 'Overdue') apiValue = 'overdue';
        
        return {
          id: index, 
          displayText: value === 'All' ? value : formatFilterDisplayText(value),
          originalValue: value === 'All' ? value : apiValue // Store API value for API calls
        };
      }),
      selectedOptionId: (() => {
        // Map current filter value (API format) back to display format for selection
        const currentStatusFilter = currentFilters.status;
        console.log('Status filter - currentStatusFilter:', currentStatusFilter);
        
        if (!currentStatusFilter || currentStatusFilter === 'All') {
          const index = uniqueStatuses.findIndex(v => v === 'All');
          console.log('Status filter - selecting "All" at index:', index);
          return index;
        }
        
        // Map API values back to display values
        let displayValue = currentStatusFilter;
        if (currentStatusFilter === 'in_progress') displayValue = 'In Progress';
        else if (currentStatusFilter === 'completed') displayValue = 'Completed';
        else if (currentStatusFilter === 'declined') displayValue = 'Declined';
        else if (currentStatusFilter === 'overdue') displayValue = 'Overdue';
        
        const index = uniqueStatuses.findIndex(v => v === displayValue);
        console.log(`Status filter - mapping "${currentStatusFilter}" to "${displayValue}" at index:`, index);
        return index;
      })(),
      placeholder: 'Select Status'
    },
    {
      key: 'priority',
      label: 'Priority',
      options: uniquePriorities.map((value, index) => ({ 
        id: index, 
        displayText: value === 'All' ? value : formatFilterDisplayText(value),
        originalValue: value // Store original value for API calls
      })),
      selectedOptionId: uniquePriorities.findIndex(v => v === currentFilters.priority),
      placeholder: 'Select Priority'
    },
    {
      key: 'assignedUser',
      label: 'Assigned User',
      options: assigneeOptions && assigneeOptions.length > 0 ? assigneeOptions : [
        { id: 0, displayText: 'All', originalValue: 'All' },
        ...uniqueAssignees.map(([gpid, displayName], index) => ({ 
          id: index + 1, 
          displayText: displayName,
          originalValue: gpid // Store GPID for API calls
        }))
      ],
      selectedOptionId: (() => {
        const currentValue = currentFilters.assignedUser;
        if (!currentValue || currentValue === 'All') {
          return 0;
        }
        
        if (assigneeOptions && assigneeOptions.length > 0) {
          // Find by matching originalValue (GPID)
          const option = assigneeOptions.find(opt => opt.originalValue === currentValue);
          return option ? option.id : 0;
        } else {
          // Fallback to original logic for backward compatibility
          const foundIndex = uniqueAssignees.findIndex(([gpid]) => gpid === currentValue);
          return foundIndex >= 0 ? foundIndex + 1 : 0;
        }
      })(),
      placeholder: 'Select Assigned User'
    },
    {
      key: 'actionPlanOwner',
      label: 'Action Plan Owner',
      options: ownerOptions && ownerOptions.length > 0 ? ownerOptions : [
        { id: 0, displayText: 'All', originalValue: 'All' },
        ...uniqueOwners.map(([gpid, displayName], index) => ({ 
          id: index + 1, 
          displayText: displayName,
          originalValue: gpid // Store GPID for API calls
        }))
      ],
      selectedOptionId: (() => {
        const currentValue = currentFilters.actionPlanOwner;
        if (!currentValue || currentValue === 'All') {
          return 0;
        }
        
        if (ownerOptions && ownerOptions.length > 0) {
          // Find by matching originalValue (GPID)
          const option = ownerOptions.find(opt => opt.originalValue === currentValue);
          return option ? option.id : 0;
        } else {
          // Fallback to original logic for backward compatibility
          const foundIndex = uniqueOwners.findIndex(([gpid]) => gpid === currentValue);
          return foundIndex >= 0 ? foundIndex + 1 : 0;
        }
      })(),
      placeholder: 'Select Owner'
    },
  
  ];
};

/**
 * Map display status to API format
 */
export const mapStatusToApi = (displayStatus: string): string => {
  const status = displayStatus.toLowerCase();
  return STATUS_DISPLAY_TO_API[status] || status;
};

/**
 * Build API request body for status update
 */
export const buildStatusUpdateRequestBody = (
  actionPlan: ActionPlanItem,
  apiStatus: string
): Record<string, any> => {
  return {
    title: actionPlan.TITLE || '',
    priority: actionPlan.PRIORITY || 'Medium',
    target_date: actionPlan.TARGET_DATE || new Date().toISOString().split('T')[0],
    notes: actionPlan.DESCRIPTION || '',
    status: apiStatus,
    updated_by: DEFAULT_USER_ID
  };
};
